import { ConfirmDialog } from "@/components/Dialogs/ConfirmDialog"
import {
	CatalogManager,
	catalogExists,
	openCatalogDirectory,
	ProjectInfoRecord,
} from "@/lib/catalog"
import { checkDirectoryIsValid, chooseAndValidateDirectory } from "@/storage/open-directory"
import { storeProjectFileHandle } from "@/storage/indexeddb"
import { replaceDialog, resetDialogs } from "../dialogs-state"
import { chooseProjectMode, isProjectSetupIncomplete, startEditing } from "./project"
import { ProjectItem } from "./projects-service"
import {
	cancelOpenProject,
	startMetadataPrefetching,
	setUpProject,
	setupProjectBaseState,
} from "./setup-project"
import { handleProjectError } from "@/store/project/handle-project-error"
import { LoadingDialog } from "@/components/Dialogs/LoadingDialog"
import { ensureSampleProjectMounted } from "./sample-project-loader"
import { store } from "../store"
import { cullingInfoAtom } from "../photo/photos-state"
import { consumeRecentProjectPreflightHandle } from "./open-project-preflight"
import { resolveSampleProjectPath } from "./sample-project-path"
import { openSession } from "@/routing/project-session"
// eslint-disable-next-line no-restricted-imports -- Phase 1: store-layer i18n
import i18n from "@/i18n"

export interface OpenProjectOptions {
	project: ProjectItem
	existingProjectInfo?: ProjectInfoRecord
}

export async function openProject(options: OpenProjectOptions) {
	replaceDialog(LoadingDialog, { title: i18n.t("store:openProject.openingProject") })

	let resolvedHandle: FileSystemDirectoryHandle | null = null

	if (options.project.isSampleProject) {
		console.info("[SampleProject] Opening sample project", options.project.id)
		try {
			// Ensure the sample project is hydrated inside OPFS before proceeding.
			const rootHandle = await ensureSampleProjectMounted(options.project.id)
			console.info("[SampleProject] Root handle resolved", rootHandle)
			options.project.handle = await resolveSampleProjectPath(rootHandle, options.project)
			console.info("[SampleProject] Resolved project handle", options.project.handle)
			resolvedHandle = options.project.handle ?? null
		} catch (error) {
			console.error("[SampleProject] Failed to resolve sample project", error)
			resetDialogs()
			void handleProjectError(error, { source: "open_project_sample_mount" })
			return
		}
	}

	if (!resolvedHandle) {
		const preflightValidatedHandle = await consumeRecentProjectPreflightHandle(
			options.project.id,
			options.project.handle,
		)
		if (preflightValidatedHandle) {
			resolvedHandle = preflightValidatedHandle
		}
	}

	if (!resolvedHandle) {
		//Check project handle
		const validProjectHandle = await checkDirectoryIsValid(options.project.handle, [
			"no_handle",
			"no_directory",
		])
		console.info("[ProjectOpen] checkDirectoryIsValid result", validProjectHandle)
		switch (validProjectHandle) {
			case "no_handle":
			case "no_directory":
				handleProjectFolderMissing(options)
				return
			case "no_permission":
			case "no_access":
				handleProjectPermissionMissing()
				return
		}

		if (!(validProjectHandle instanceof FileSystemDirectoryHandle)) {
			resetDialogs()
			return
		}

		resolvedHandle = validProjectHandle
	}

	options.project.handle = resolvedHandle

	try {
		const hasCatalog = await catalogExists(resolvedHandle)
		if (!hasCatalog) {
			await handleCatalogMissing(resolvedHandle, options.project)
			return
		}

		//Open project and fetch project info
		await openCatalogDirectory(resolvedHandle)
		let projectInfo = options.existingProjectInfo ?? (await CatalogManager.fetchProjectInfo())
		if (projectInfo && projectInfo.id !== options.project.id) {
			//Update catalog's project info with BE id if its out of sync
			const oldIdToBeReplaced = projectInfo.id
			projectInfo = { ...projectInfo, id: options.project.id }
			await CatalogManager.storeProjectInfo({
				record: projectInfo,
				oldIdToBeReplaced,
			})
		}

		if (projectInfo) {
			if (isProjectSetupIncomplete(projectInfo)) {
				await startMetadataPrefetching(options.project, resolvedHandle)

				chooseProjectMode({
					handle: resolvedHandle,
					disableClose: false,
					existingProject: options.project,
				})
				return
			} else {
				// If not prefetching, fetch existing culling info from the catalog to avoid clustering-dialog re-appearing
				const cullingInfoRecords = await CatalogManager.fetchCullingInfoRecords()
				store.set(cullingInfoAtom, cullingInfoRecords)
			}

			//Style info stored in catalog
			const styleInfo = await CatalogManager.fetchStyleInfo()
			const localProjectStyle = await CatalogManager.fetchProjectStyle()

			let styleMatchesCatalog = true
			if (styleInfo) {
				styleMatchesCatalog = styleInfo.styleId === projectInfo.styleId
			} else if (localProjectStyle) {
				styleMatchesCatalog = localProjectStyle.id === projectInfo.styleId
			}

			if (styleMatchesCatalog) {
				await setUpProject({
					handle: resolvedHandle,
					aiStyleTriggerSource: "resume_project",
					needsSetup: projectInfo.needsSetup ?? undefined,
					existingProjectInfo: projectInfo,
					existingProject: options.project,
					existingProjectStyle: styleInfo ?? localProjectStyle,
				})
			} else {
				await handleStyleMissing(resolvedHandle, options.project)
			}
		} else {
			await handleCatalogMissing(resolvedHandle, options.project)
		}
	} catch (error) {
		void handleProjectError(error, { source: "open_project" })
		return
	}
}

export function handleProjectFolderMissing(options: OpenProjectOptions) {
	replaceDialog(ConfirmDialog, {
		title: i18n.t("store:openProject.projectFolderMissingTitle"),
		message: i18n.t("store:openProject.projectFolderMissingMessage"),
		secondary: {
			title: i18n.t("common:action.cancel"),
			handler: () => {
				cancelOpenProject("missing_project_folder_cancelled")
				resetDialogs()
			},
		},
		primary: {
			title: i18n.t("store:openProject.selectProjectFolder"),
			handler: async () => {
				const newDirectory = await chooseAndValidateDirectory("import_photo")
				if (newDirectory instanceof FileSystemDirectoryHandle) {
					options.project.handle = newDirectory
					try {
						await storeProjectFileHandle({
							id: options.project.id,
							handle: newDirectory,
						})
						await openSession(options)
					} catch (error) {
						void handleProjectError(error, { source: "open_project_select_folder" })
					}
				} else {
					cancelOpenProject("missing_project_folder_picker_cancelled")
					resetDialogs()
				}
			},
		},
		trackingViewName: "missing_project_folder_dialog",
	})
}

export function handleProjectPermissionMissing() {
	resetDialogs()
	cancelOpenProject("missing_project_permission")
}

async function handleStyleMissing(handle: FileSystemDirectoryHandle, project: ProjectItem) {
	await setupProjectBaseState(project, { handle })
	//TODO: SIYAO should this update to allow editing without style?
	replaceDialog(ConfirmDialog, {
		title: i18n.t("store:openProject.styleMissingTitle"),
		message: i18n.t("store:openProject.styleMissingMessage"),
		secondary: {
			title: i18n.t("common:action.cancel"),
			handler: () => {
				cancelOpenProject("style_missing_cancelled")
				resetDialogs()
			},
		},
		primary: {
			title: i18n.t("store:openProject.selectStyle"),
			handler: () =>
				startEditing({
					handle,
					existingProject: project,
					disableClose: false,
					clearExistingEdits: true,
				}),
			disablePopDialog: true,
		},
		trackingViewName: "missing_style_dialog",
	})
}

async function handleCatalogMissing(handle: FileSystemDirectoryHandle, project: ProjectItem) {
	if (project.isSampleProject) {
		await openCatalogDirectory(handle)
		await startMetadataPrefetching(project, handle)
		chooseProjectMode({
			handle,
			existingProject: project,
			disableClose: false,
		})
		return
	}

	await setupProjectBaseState(project, { handle: handle })

	replaceDialog(ConfirmDialog, {
		title: i18n.t("store:openProject.catalogMissingTitle"),
		message: i18n.t("store:openProject.catalogMissingMessage"),
		secondary: {
			title: i18n.t("common:action.cancel"),
			handler: () => {
				cancelOpenProject("catalog_missing_cancelled")
				resetDialogs()
			},
		},
		primary: {
			title: i18n.t("store:openProject.createNew"),
			handler: () =>
				chooseProjectMode({
					handle,
					existingProject: project,
					disableClose: false,
				}),
		},
		trackingViewName: "missing_catalog_dialog",
	})
}
